import { Injectable } from '@jkhy/frontend-utils/di';
import { createUniqueId, exists } from '@jkhy/frontend-utils/functions';
import {
    CancelWireModelDto,
    CompanyAccountModelDto,
    CompanyAccountsClient,
    CountriesClient,
    FileFormatModelDto,
    FileImportLayoutFieldModelDto,
    FrequencyModelDto,
    LookupListModelDto,
    RequestQuoteModelDto,
    SecurityMessageModelDto,
    WireCompaniesClient,
    WireFileSearchCriteriaModelDto,
    WireModelDto,
    WireParseResultModelDto,
    WiresClient,
    WireSearchCriteriaModelDto,
    WireTemplateModelDto,
} from '@treasury/api/channel';
import { AnalyticsEvent, AnalyticsService } from '@treasury/core/analytics';
import { TmHttpClient } from '@treasury/core/http/tm-http-client';
import { format } from 'date-fns';
import {
    Wire,
    WireAccount,
    WireCompany,
    WireConfiguration,
    WireQuote,
    WireSearchCriteria,
    WireTemplate,
    WireTemplateQueryDto,
} from '.';
import { DownloadFormat, DownloadService } from '../channel/services/download';
import { FiDate, Holiday, HolidaysService } from '../dates';
import { isDateInArrayOfBankingHolidays } from '../shared';
import {
    FieldsArray,
    mapRequestToUploadFormatParameters,
    mapResponseToGlossaryAndTiles,
    mapResponseToWireUploadFormat,
    WireUploadFormatUIModelReturn,
} from './mappings/wire-upload-formats.mappings';
import { WireCountry } from './wire-country.entity';
import { WireFileProcessed } from './wire-file-processed.entity';
import { WireFileUpload } from './wire-file-upload.entity';
import { WireFile } from './wire-file.entity';
import { WireUploadFormat } from './wire-upload-format.entity';
import { getDayForADate, getDayForAnnual, getDaysOfMonth, getStartAndEnd } from './wires.helpers';
import {
    fetchTransferAccounts,
    fetchWireTemplateApprovers,
    searchTemplates,
} from './wires.requests';

export type WiresFilter = WireSearchCriteriaModelDto;
export type WireFileFilter = WireFileSearchCriteriaModelDto;
@Injectable()
export class WiresService {
    constructor(
        private readonly wiresClient: WiresClient,
        private readonly wireCompaniesClient: WireCompaniesClient,
        private readonly analytics: AnalyticsService,
        private readonly companyAccountsClient: CompanyAccountsClient,
        private readonly holidaysService: HolidaysService,
        private readonly countriesClient: CountriesClient
    ) {}

    /** FIXME:
     * There's an extra space in all but the OneTime case
     * The possibility of startOn defaulting to '' is just throwing an error, should there be a guard
     * The default case is just returning a '.' should it instead return undefined or ''
     */
    public static summarizeFrequency(frequency: FrequencyModelDto) {
        switch (this.frequencyTypeToFriendly(frequency.type)) {
            case 'One Time':
                return 'One Time.';
            case 'Weekly':
                return `Occurs  every ${frequency.repeatOn} ${getStartAndEnd(frequency)}.`;
            case 'Every Two Weeks':
                return `Occurs  on ${frequency.repeatOn} every two weeks ${getStartAndEnd(frequency)}.`;
            case 'Twice a Month':
                return `Occurs  on ${getDaysOfMonth(frequency, true)} every month ${getStartAndEnd(frequency)}.`;
            case 'Monthly':
                return `Occurs  on ${getDaysOfMonth(frequency)} of every month ${getStartAndEnd(frequency)}.`;
            case 'Quarterly':
                return `Occurs  on the ${getDayForADate(frequency.startOn || '')} every three months ${getStartAndEnd(frequency)}.`;
            case 'Every Six Months':
                return `Occurs  on the ${getDayForADate(frequency.startOn || '')} every six months ${getStartAndEnd(frequency)}.`;
            case 'Yearly':
                return `Occurs  on ${getDayForAnnual(frequency.startOn || '')} every year ${getStartAndEnd(frequency)}.`;
            default:
                return '.';
        }
    }

    public static frequencyTypeToFriendly(frequencyType?: string) {
        if (!exists(frequencyType)) {
            return 'Undefined';
        }

        switch (frequencyType) {
            case 'EveryTwoWeeks':
                return 'Every Two Weeks';
            case 'TwiceAMonth':
                return 'Twice a Month';
            case 'EverySixMonths':
                return 'Every Six Months';
            case 'OneTime':
                return 'One Time';
            default:
                return frequencyType;
        }
    }

    public async checkValidDateTime() {
        const date = new Date();
        const [cutoffTimes, holidays] = await Promise.all([
            this.companyAccountsClient.companyAccountsGetCutoffTimes(),
            this.holidaysService.getHolidays(),
        ]);
        const currentFiTime = (cutoffTimes.data.currentFiTime ?? '').split('T').pop();
        const wireCutoff = cutoffTimes.data.processingCutoffs?.find(
            obj => obj.productType === 'Wire'
        );

        const getNextBusinessDay = (holidays: Array<Holiday>, startDate: Date = new Date()) => {
            // Create a new date to avoid mutating the input
            const date = new Date(startDate);

            // Move to next day
            date.setDate(date.getDate() + 1);

            // Skip weekends - use while loop to handle consecutive weekend days
            while (date.getDay() === 0 || date.getDay() === 6) {
                date.setDate(date.getDate() + 1);
            }

            // Skip holidays - recursively check after each skip
            while (isDateInArrayOfBankingHolidays(holidays, date)) {
                date.setDate(date.getDate() + 1);
                // Check again for weekends after skipping holiday
                while (date.getDay() === 0 || date.getDay() === 6) {
                    date.setDate(date.getDate() + 1);
                }
            }

            return date.toISOString();
        };

        return {
            isAfterCutoff:
                !currentFiTime || !wireCutoff
                    ? false
                    : currentFiTime > (wireCutoff.cutoffTime ?? ''),
            isHoliday: isDateInArrayOfBankingHolidays(holidays, date),
            isWeekend: [0, 6].includes(date.getDay()),
            nextBusinessDay: getNextBusinessDay(holidays, date),
        };
    }

    public async getValidDefaultFrequency() {
        const defaultFrequency: FrequencyModelDto = {
            type: 'OneTime',
            valueDate: format(new Date(), 'MM/dd/yyyy'),
            id: 0,
            updatedDate: '',
        };
        const { isAfterCutoff, isHoliday, isWeekend, nextBusinessDay } =
            await this.checkValidDateTime();
        if (isHoliday || isWeekend) {
            return {
                ...defaultFrequency,
                valueDate: nextBusinessDay,
            };
        }
        return {
            ...defaultFrequency,
            valueDate: isAfterCutoff ? nextBusinessDay : defaultFrequency.valueDate,
        };
    }

    public async getWirePaymentDetails(id: number) {
        const response = await this.wiresClient.wiresGet(id);
        return new Wire(response.data);
    }

    public async createWirePayment(wire: Wire, template: boolean = false) {
        if (!wire.duplicatePreventionId) {
            wire.duplicatePreventionId = createUniqueId();
        }
        const response = await this.wiresClient.wiresPost(
            wire.toCreateDto(template) as WireModelDto
        );
        return response.data;
    }

    public async createMultipleWires(wires: Wire[]) {
        const wireListDto = {
            id: 0,
            updatedDate: '',
            wires: wires.map(wire => {
                if (!wire.duplicatePreventionId) {
                    wire.duplicatePreventionId = createUniqueId();
                }
                return wire.toCreateDto(false) as WireModelDto;
            }),
        };
        const response = await this.wiresClient.wiresInitiateBulkWires(wireListDto);
        return response.data;
    }

    public async getWireCompanies(nonDli = false) {
        const response = await this.wireCompaniesClient.wireCompaniesGet(
            { nonDli },
            {
                maxAgeInSeconds: 600,
            }
        );
        return response.data.map(
            wireCompany => new WireCompany(wireCompany) as unknown as WireCompany
        );
    }

    public async getDebitAccounts(id: number, type: string) {
        const response = await this.wireCompaniesClient.wireCompaniesAccounts(
            id,
            { type },
            {
                maxAgeInSeconds: 600,
            }
        );
        return response.data.map(
            wireAccount => new WireAccount(wireAccount) as unknown as WireAccount
        );
    }

    public async getWireCountries() {
        const response = await this.countriesClient.countriesGet({ maxAgeInSeconds: 600 });
        return response.data.map(country => new WireCountry(country) as unknown as WireCountry);
    }

    public async getWireCurrencies() {
        const response = await this.wiresClient.wiresGetInternationalWireCurrencies({
            maxAgeInSeconds: 6000,
        });
        return response.data;
    }

    public async getNewWireQuote(model: RequestQuoteModelDto) {
        const response = await this.wiresClient.wiresGetNewWireQuote(model);
        return new WireQuote(response.data);
    }

    public async createFxWire(wire: WireModelDto) {
        const response = await this.wiresClient.wiresAcceptNewWireQuote(wire);
        return new Wire(response.data);
    }

    public async getWireConfiguration() {
        const response = await this.wireCompaniesClient.wireCompaniesWireConfiguration({
            maxAgeInSeconds: 600,
        });
        const config = new WireConfiguration(response.data);
        return config;
    }

    public async getTemplate(id: number) {
        const response = await this.wiresClient.wiresGetTemplate(id);
        return new WireTemplate(response.data);
    }

    public async searchTemplates(query: WireTemplateQueryDto) {
        const dtos = (await searchTemplates(query)) || [];
        const wireTemplate = dtos.map(dto => new WireTemplate(dto));
        return wireTemplate.map(template => {
            template.status = template.isPendingEdit ? 'Pending Edits' : template.status;
            return template;
        });
    }

    public async validateTemplateName(name: string) {
        const response = await this.wiresClient.wiresValidateTemplateName({ templateName: name });
        return response.data;
    }

    public async wiresCreateTemplate(wireTemplate: WireTemplate) {
        const response = await this.wiresClient.wiresCreateTemplate(
            wireTemplate.toDto() as WireTemplateModelDto
        );
        return response.data;
    }

    public async wiresUpdateTemplate(wireTemplate: WireTemplate) {
        const response = await this.wiresClient.wiresUpdateTemplate(
            wireTemplate.id,
            wireTemplate.toDto() as WireTemplateModelDto
        );
        return response.data;
    }

    public async wiresDeleteTemplate(id: number) {
        const response = await this.wiresClient.wiresDeleteTemplate(id);
        return response.data;
    }

    public async downloadWires(
        outputType: DownloadFormat,
        page: string,
        filterModel: Omit<WireSearchCriteriaModelDto, 'updatedDate'>
    ) {
        const fileSuffix = new FiDate(new Date()).toIsoDate();
        const downloadFormats: DownloadFormat[] = [
            'Summary - PDF',
            'Detail - PDF',
            'Summary - CSV',
            'Detail - CSV',
        ];
        return DownloadService.download<WireSearchCriteria>(
            fileSuffix,
            outputType,
            page,
            { ...filterModel, page },
            downloadFormats
        );
    }

    // FIXME: This has no corollary in the CompanyAccounts client
    public async fetchTransferAccounts() {
        return (await fetchTransferAccounts()) as CompanyAccountModelDto[];
    }

    public async fetchWireTemplateApprovers(template: WireTemplate) {
        await fetchWireTemplateApprovers(template);
    }

    public async fetchWireActivity(wireSearchCriteria: WireSearchCriteriaModelDto, type: string) {
        const response = await this.wiresClient.wiresSearch(wireSearchCriteria, { type });
        return response.data.map(wire => new Wire(wire) as Wire);
    }

    public async fetchWireActivityWithTotals(
        wireSearchCriteria: WireSearchCriteriaModelDto,
        query = { type: 'One-Time' }
    ) {
        const { data } = await this.wiresClient.wiresSearchWithTotals(wireSearchCriteria, query);
        return {
            data: data.searchResults?.map(wire => new Wire(wire) as Wire),
            totals: data.totals as { totalAmount: number; count: number; status: string }[],
        };
    }

    public async fetchOneTimeWires(
        wireSearchCriteria: WireSearchCriteriaModelDto,
        query = { type: 'One Time' }
    ) {
        return this.wiresClient.wiresSearch(wireSearchCriteria, query);
    }

    public async fetchRecurringWires(
        wireSearchCriteria: WireSearchCriteriaModelDto,
        query = { type: 'Recurring' }
    ) {
        return this.wiresClient.wiresSearch(wireSearchCriteria, query);
    }

    public async fetchWireFileActivity(wireFileSearchCriteria: WireFileSearchCriteriaModelDto) {
        const response = await this.wiresClient.wiresSearchFile(wireFileSearchCriteria);
        return response.data.items?.map(file => new WireFile(file) as WireFile);
    }

    public async fetchWireUploadUsers() {
        const response = await this.wiresClient.wiresSearchFileAll();
        return response.data;
    }

    public async search(criteria: WireSearchCriteria, type: 'One Time' | 'Recurring') {
        const response = await this.wiresClient.wiresSearch(
            criteria as WireSearchCriteriaModelDto,
            { type }
        );
        return response.data.map(wireDto => new Wire(wireDto));
    }

    public async searchWithTotals(criteria: WireSearchCriteria, type: 'One Time' | 'Recurring') {
        const response = await this.wiresClient.wiresSearchWithTotals(
            criteria as WireSearchCriteriaModelDto,
            { type }
        );
        const data = response.data.searchResults?.map(wireDto => new Wire(wireDto));
        const summary = response.data.totals?.map(summaryDto => ({
            title: summaryDto.status ?? '',
            count: summaryDto.count,
            totalAmount: summaryDto.totalAmount,
            totalCredit: undefined,
            totalDebit: undefined,
        }));
        return { data: data ?? [], summary, totalCount: data?.length ?? 0 };
    }

    public async deleteWirePayment(deleteRequest: CancelWireModelDto) {
        return this.wiresClient.wiresDelete(deleteRequest);
    }

    public async batchApproveOrReject(batch: LookupListModelDto, type: string) {
        const actionType = type === 'approve' ? 'Approve WIRE Payment' : 'Reject WIRE Payment';
        // API Expects first letter of type to be capitalized
        const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1);
        const securityMessage: SecurityMessageModelDto = {
            actionType,
            hasAlternate: false,
            challengeMethodTypeId: 0,
        };

        const p = this.wiresClient.wiresBatch(
            0,
            { ...batch, securityMessage },
            { action: capitalizedType }
        );

        if (type === 'approve') {
            p.then(() => {
                const { lookups: wires } = batch;
                wires?.forEach(({ id }) => {
                    this.analytics.track(AnalyticsEvent.WireApproved, {
                        id: id.toString(),
                    });
                });
            });
        }

        return p;
    }

    /**
     *
     * Wire upload formats calls
     */
    public async fetchWireUploadFormats() {
        const response = await this.wiresClient.wiresGetFileFormats();
        return response.data.map(format => new WireUploadFormat(format));
    }

    public async fetchWireUploadFormat(id: number, fields: FieldsArray) {
        const response = await this.wiresClient.wiresGetFileFormat(id);
        return mapResponseToWireUploadFormat(
            response.data as FileFormatModelDto,
            fields
        ) as WireUploadFormatUIModelReturn;
    }

    public async deleteWireUploadFormat(id: number) {
        return this.wiresClient.wiresDeleteFileFormat(id);
    }

    public async createWireUploadFormat(format: WireUploadFormatUIModelReturn) {
        const mappedRequest = mapRequestToUploadFormatParameters(format);
        const response = await this.wiresClient.wiresCreateFileFormat(mappedRequest);
        return response.data;
    }

    public async updateWireUploadFormat(format: WireUploadFormatUIModelReturn) {
        const mappedRequest = mapRequestToUploadFormatParameters(format);
        const response = await this.wiresClient.wiresUpdateFileFormat(format.id, mappedRequest);
        return response.data;
    }

    public async getWireUploadFormatFields() {
        const response = await this.wiresClient.wiresGetFileFormatFields();
        return mapResponseToGlossaryAndTiles(response.data as FileImportLayoutFieldModelDto);
    }

    /**
     * Wire upload calls
     * Wire upload also utilizes:
     *  getWireCompanies (nonDil true)
     * fetchWireUploadFormats
     * getWireConfiguration
     * createMultipleWires
     */
    public async uploadWireFile(file: File, companyId: string, formatId: string) {
        const http = await TmHttpClient.getInstance();
        const fd = new FormData();
        fd.append('wireData', file, file.name);
        fd.append('wireCompanyId', companyId);
        fd.append('formatId', formatId);
        const response = await http.request('wires/parseWireFile', {
            method: 'POST',
            body: fd,
            disableContentType: true,
        });
        return new WireFileUpload(response as WireParseResultModelDto);
    }

    public async processWireFile(fileId: number) {
        const id = fileId.toString();
        const response = await this.wiresClient.wiresProcessWireFile(id);
        return new WireFileProcessed(response.data);
    }

    public async getAccountConfiguration() {
        return (await TmHttpClient.getInstance()).request(`account`, {
            method: 'GET',
        });
    }

    public async getHolidays() {
        const response = await this.holidaysService.getHolidays();
        return response;
    }
}
